/**
 * Página de listagem de itens da previsão ativa
 * Exibe todos os produtos com suas métricas de previsão, pedido, estoque e percentual
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Box, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { ViewModule, ViewList } from '@mui/icons-material';
import { Layout, Loading, ErrorMessage, SearchBar, EmptyState, PageHeader } from '@components';
import { ProdutoItemGrid } from '@components/produto/ProdutoItemGrid';
import { ProdutoItemList } from '@components/produto/ProdutoItemList';
import { useSession } from '@hooks';
import { Inventory } from '@mui/icons-material';
import { Produto } from '@types';
import { api } from '@services/api';
import { API_ENDPOINTS } from '@types';
import theme from '@theme';

type ViewMode = 'grid' | 'list';
type OrderBy = 'nome-asc' | 'nome-desc' | 'percentual-asc' | 'percentual-desc';

const ListaItensPrevisaoContent: React.FC = () => {
  const navigate = useNavigate();
  const { vendedor } = useSession();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [orderBy, setOrderBy] = useState<OrderBy>('nome-asc');
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Carrega todos os produtos da previsão (sem filtro de classificação)
  useEffect(() => {
    const loadProdutos = async () => {
      if (!vendedor || !vendedor.vendCodigo) {
        setError(new Error('Vendedor não encontrado'));
        setIsLoading(false);
        return;
      }

      // Verifica se há uma previsão ativa
      const previsaoAtiva = vendedor.indPrevisao && vendedor.indPrevisao > 0;

      if (!previsaoAtiva) {
        setError(new Error('Nenhuma previsão ativa encontrada'));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const params = {
          vendedor: vendedor.vendCodigo,
          previsao: vendedor.indPrevisao,
        };

        console.log('[ITENS PREVISAO] Chamando API obterProdutoPrevisao:', {
          endpoint: API_ENDPOINTS.OBTER_PRODUTO_PEDIDO,
          params,
        });

        const response = await api.post<Produto[]>(
          API_ENDPOINTS.OBTER_PRODUTO_PEDIDO,
          params
        );

        console.log('[ITENS PREVISAO] Resposta da API:', response);

        setProdutos(response || []);
      } catch (err) {
        console.error('[ITENS PREVISAO] Erro ao carregar produtos:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProdutos();
  }, [vendedor]);

  // Filtra produtos por termo de busca
  const filteredData = React.useMemo(() => {
    if (!searchTerm) return produtos;

    const termoLower = searchTerm.toLowerCase();
    return produtos.filter(
      (p) =>
        p.nome.toLowerCase().includes(termoLower) ||
        p.codigo.toLowerCase().includes(termoLower)
    );
  }, [produtos, searchTerm]);

  // Ordena produtos
  const sortedData = React.useMemo(() => {
    const [field, direction] = orderBy.split('-') as ['nome' | 'percentual', 'asc' | 'desc'];

    const sorted = [...filteredData].sort((a, b) => {
      if (field === 'nome') {
        return direction === 'asc'
          ? a.nome.localeCompare(b.nome)
          : b.nome.localeCompare(a.nome);
      } else {
        // Ordena por percentual (pedido / previsão * 100)
        const percA = a.previsao > 0 ? (a.pedido / a.previsao) * 100 : 0;
        const percB = b.previsao > 0 ? (b.pedido / b.previsao) * 100 : 0;
        return direction === 'asc' ? percA - percB : percB - percA;
      }
    });

    return sorted;
  }, [filteredData, orderBy]);

  const refetch = useCallback(() => {
    window.location.reload();
  }, []);

  if (isLoading) {
    return <Loading message="Carregando itens da previsão..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Erro ao carregar itens"
        message={error.message}
        onRetry={refetch}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Itens da Previsão"
        subtitle={`${sortedData.length} produtos na previsão ativa`}
        showBackButton
        onBack={() => navigate(-1)}
      />

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: 1 }, order: { xs: 1, sm: 1 } }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar produto..."
          />
        </Box>

        <FormControl sx={{ minWidth: 200, display: { xs: 'none', md: 'flex' }, order: { sm: 2 } }} size="small">
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={orderBy}
            label="Ordenar por"
            onChange={(e) => setOrderBy(e.target.value as OrderBy)}
            sx={{ bgcolor: theme.palette.elite.contrastText }}
          >
            <MenuItem value="nome-asc">Nome (A-Z)</MenuItem>
            <MenuItem value="nome-desc">Nome (Z-A)</MenuItem>
            <MenuItem value="percentual-desc">Maior % vendido</MenuItem>
            <MenuItem value="percentual-asc">Menor % vendido</MenuItem>
          </Select>
        </FormControl>

        {/* Toggle de visualização */}
        <Box sx={{ display: 'flex', gap: 0.5, order: { xs: 2, sm: 3 } }}>
          <Tooltip title="Visualização em Grade">
            <IconButton
              color={viewMode === 'grid' ? 'primary' : 'default'}
              onClick={() => setViewMode('grid')}
              size="large"
            >
              <ViewModule />
            </IconButton>
          </Tooltip>
          <Tooltip title="Visualização em Lista">
            <IconButton
              color={viewMode === 'list' ? 'primary' : 'default'}
              onClick={() => setViewMode('list')}
              size="large"
            >
              <ViewList />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {sortedData.length === 0 ? (
        <EmptyState
          title="Nenhum produto encontrado"
          message={
            searchTerm ? 'Tente ajustar sua busca' : 'Não há produtos na previsão ativa'
          }
          icon={<Inventory />}
        />
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {sortedData.map((produto) => (
            <Grid item xs={6} sm={6} md={3} key={produto.codigo}>
              <ProdutoItemGrid produto={produto} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={1}>
          {sortedData.map((produto) => (
            <Grid item xs={12} md={6} key={produto.codigo}>
              <ProdutoItemList produto={produto} />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export const ListaItensPrevisaoPage: React.FC = () => {
  return (
    <Layout title="Itens da Previsão" showHeader showFooter>
      <ListaItensPrevisaoContent />
    </Layout>
  );
};

export default ListaItensPrevisaoPage;
